// Words the parts work out: email addresses typed or pasted, an MFD's pairing code, initials and sizes.

const EMAIL = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

/** Whether text is an email address, as far as a person typing can tell. */
export function isEmail(text: string): boolean {
  return EMAIL.test(text);
}

/**
 * The addresses in text typed or pasted: split at commas, semicolons, lines and spaces, each once, in lower case. A
 * name with its address in angle brackets ("Sam Lee <sam@example.com>") gives the address.
 */
export function parseEmails(text: string, already: readonly string[] = []): string[] {
  const seen = new Set(already.map((e) => e.toLowerCase()));
  const out: string[] = [];
  for (const entry of text.split(/[,;\n]+/)) {
    const bracketed = /<([^>]*)>/.exec(entry)?.[1];
    for (const raw of bracketed !== undefined ? [bracketed] : entry.split(/\s+/)) {
      const email = raw
        .replace(/^mailto:/i, '')
        .trim()
        .toLowerCase();
      if (email && !seen.has(email)) {
        seen.add(email);
        out.push(email);
      }
    }
  }
  return out;
}

/** The code a TUNE MFD shows: eight letters, written XXXX-XXXX. */
export function formatPairCode(text: string): string {
  const letters = text
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 8);
  return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}

/** Up to two initials from a name, or from an email before its @. */
export function initials(name: string | null | undefined, email?: string | null): string {
  const source = (name?.trim() || email?.split('@')[0] || '?').trim();
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}` : source.slice(0, 2);
  return letters.toUpperCase();
}

/** Bytes as people read them: "2.4 MB". */
export function bytesText(n: number): string {
  if (n < 1000) {
    return n === 1 ? '1 byte' : `${n} bytes`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1000;
  let u = 0;
  while (v >= 999.5 && u < units.length - 1) {
    v /= 1000;
    u += 1;
  }
  return `${v.toLocaleString('en-AU', { maximumFractionDigits: v < 10 ? 1 : 0 })} ${units[u]}`;
}
